/**
 * F31 (Wave 2B) — admin sport-skill-descriptor edit dialog (CMS).
 *
 * Add mode: sport + band-min + band-max are editable Selects/inputs.
 * Edit mode: sport + band metadata are read-only (the unique key is
 * `(sport, levelBandMin)`); only the body text changes.
 *
 * Validates against `UpsertSportSkillDescriptorInputSchema` (bodyText 20–2000,
 * bandMax > bandMin, 0.1 precision). Live character counter on the Textarea.
 *
 * Composes F02 primitives only (Modal / FormField / Select / Textarea / Button
 * / Banner). Posts via the parent's `onSave` which calls the CMS upsert.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Select, Textarea } from '@bravemove/ui-web';
import {
  SPORT_NAME_VALUES,
  UpsertSportSkillDescriptorInputSchema,
  type SportSkillDescriptor,
  type UpsertSportSkillDescriptorInput,
} from '@bravemove/types';
import type { SportName } from '@bravemove/design-tokens/icons';

const BAND_MIN_VALUES = [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0, 6.5];

export interface DescriptorEditDialogProps {
  readonly open: boolean;
  /** When provided, the dialog is in edit mode (sport + band read-only). */
  readonly descriptor?: SportSkillDescriptor | null;
  readonly onSave: (input: UpsertSportSkillDescriptorInput) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function DescriptorEditDialog({
  open,
  descriptor,
  onSave,
  onClose,
  errorMessage,
}: DescriptorEditDialogProps) {
  const { t } = useI18n();
  const isEdit = Boolean(descriptor);

  const [sport, setSport] = useState<SportName>(SPORT_NAME_VALUES[0] as SportName);
  const [bandMin, setBandMin] = useState(1.0);
  const [bodyText, setBodyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (descriptor) {
      setSport(descriptor.sportName as SportName);
      setBandMin(descriptor.levelBandMin);
      setBodyText(descriptor.bodyText);
    } else {
      setSport(SPORT_NAME_VALUES[0] as SportName);
      setBandMin(1.0);
      setBodyText('');
    }
  }, [open, descriptor]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const bandMax = useMemo(() => Math.round((bandMin + 0.5) * 10) / 10, [bandMin]);

  const payload: UpsertSportSkillDescriptorInput = useMemo(
    () => ({ sportName: sport, levelBandMin: bandMin, levelBandMax: bandMax, bodyText }),
    [sport, bandMin, bandMax, bodyText],
  );
  const valid = useMemo(
    () => UpsertSportSkillDescriptorInputSchema.safeParse(payload).success,
    [payload],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSave(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const sportOptions = useMemo(
    () => SPORT_NAME_VALUES.map((s) => ({ value: s, label: s.replace(/-/g, ' ') })),
    [],
  );
  const bandOptions = useMemo(
    () =>
      BAND_MIN_VALUES.map((b) => ({
        value: String(b),
        label: `${b.toFixed(1)}–${(b + 0.5).toFixed(1)}`,
      })),
    [],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      // TGS-13 — branch the title (and submit label below) on mode so the
      // create flow opened from the "Add descriptor" CTA doesn't read "Edit".
      title={
        isEdit
          ? t('skill:admin.sport_skill_descriptors.edit.title', {
              defaultValue: 'Edit descriptor',
            })
          : t('admin:skill.descriptors.add_title', { defaultValue: 'Add descriptor' })
      }
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="descriptor-edit-cancel"
          >
            {t('skill:outcome.dialog.confirm.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="descriptor-edit-submit"
          >
            {isEdit
              ? t('skill:admin.sport_skill_descriptors.edit.submit', { defaultValue: 'Save' })
              : t('admin:skill.descriptors.add_submit', { defaultValue: 'Add' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="descriptor-edit-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('skill:admin.sport_skill_descriptors.edit.sport_label', {
            defaultValue: 'Sport',
          })}
          required
        >
          <Select
            value={sport}
            onChange={(e) => setSport(e.target.value as SportName)}
            options={sportOptions}
            disabled={isEdit}
            data-testid="descriptor-edit-sport"
          />
        </FormField>
        <FormField
          label={t('skill:admin.sport_skill_descriptors.edit.band_label', {
            defaultValue: 'Level band',
          })}
          required
        >
          <Select
            value={String(bandMin)}
            onChange={(e) => setBandMin(Number(e.target.value))}
            options={bandOptions}
            disabled={isEdit}
            data-testid="descriptor-edit-band"
          />
        </FormField>
        <FormField
          label={t('skill:admin.sport_skill_descriptors.edit.body_label', {
            defaultValue: 'Description',
          })}
          required
          helper={t('admin:skill.descriptors.char_count', {
            count: bodyText.length,
            defaultValue: `${bodyText.length} / 2000`,
          })}
        >
          <Textarea
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            placeholder={t('skill:admin.sport_skill_descriptors.edit.body_placeholder', {
              defaultValue: 'Describe what this level means for this sport (20–2000 characters)',
            })}
            rows={5}
            maxLength={2000}
            data-testid="descriptor-edit-body"
          />
        </FormField>
      </div>
    </Modal>
  );
}
